import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { Select } from '@/components/Controls/Select';
import { FormManagementProps } from '@/type/form.types';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { useGetCompaniesSelectOptions } from '@/hooks/company.hooks';
import { useGetBranchesSelectOptions } from '@/hooks/branch.hooks';
import {
  useCreateVehicule,
  useGetExistingVehicule,
  useGetVehiculesSelectOptions,
} from '@/hooks/vehicule.hooks';
import { Switch } from '@/components/Controls/Switch';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { NotFound } from '@/components/Message/NotFound';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { ActionUserType } from '@/api/entities/vehicule-check';
import {
  FRENCH_LICENSE_PLATE_NUMBER_REGEX,
  SPACE_REGEX,
  VIN_REGEX,
} from '@/utils/regex.utils';
import { Input } from '@/components/Controls/Input';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { ErrorMessage } from '@/components/Message/ErrorMessage';
import dayjs from 'dayjs';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import Loader from '@/components/Loading/Loader';
import { FieldError } from '@/type/error.types';
import { generateRandomUUID } from '@/utils/random-uuid.utils';
import { useUserRole } from '@/hooks/auth.hooks';
import { VehiculeOwnerType } from '@/api/entities/vehicule';

export const VehiculeCheckInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const [assignedToVehicule, setAssignedToVehicule] = useState(
    Boolean(data?.vehiculeId),
  );
  const [licensePlateNumberValue, setLicensePlateNumberValue] = useState('');
  const [vinValue, setVinValue] = useState('');
  const [licensePlateNumberError, setLicensePlateNumberError] = useState('');
  const [vinError, setVinError] = useState('');
  const [brandValue, setBrandValue] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelValue, setModelValue] = useState('');
  const [modelError, setModelError] = useState('');

  const { isAdmin, isGroupManager, isCompanyManager, isBranchManager } =
    useUserRole();

  const canAccessGroups = isAdmin || isGroupManager;
  const canAccessCompanies = isAdmin || isGroupManager || isCompanyManager;
  const canAccessBranches =
    isAdmin || isGroupManager || isCompanyManager || isBranchManager;

  const groupIsRequired = canAccessGroups;
  const companyIsRequired = !canAccessGroups && canAccessCompanies;
  const branchIsRequired =
    !canAccessGroups && !canAccessCompanies && canAccessBranches;

  /**
   * === Set Value Watchers section ===
   */
  const actionUserTypeValue =
    watch &&
    watch('actionUserType' as Path<TSchema>, data?.actionUserType ?? '');

  const referenceNumberValue =
    watch &&
    watch('referenceNumber' as Path<TSchema>, data?.referenceNumber ?? '');

  const groupIdValue =
    watch && watch('groupId' as Path<TSchema>, data?.groupId ?? '');

  const companyIdValue =
    watch && watch('companyId' as Path<TSchema>, data?.companyId ?? '');

  const branchIdValue =
    watch && watch('branchId' as Path<TSchema>, data?.branchId ?? '');

  const vehiculeIdValue =
    watch && watch('vehiculeId' as Path<TSchema>, data?.vehiculeId ?? '');

  const humanModerationStatusValue =
    watch &&
    watch(
      'humanModerationStatus' as Path<TSchema>,
      data?.humanModerationStatus ?? HumanModerationStatus.A_MODERER,
    );
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingVehicule } = useGetExistingVehicule({
    id: data?.id,
    ownerGroupId: groupIdValue,
    ownerCompanyId: companyIdValue,
    vin: vinValue,
    licensePlateNumber: licensePlateNumberValue,
  });

  const { getGroupsSelectOptions, isLoadingAllGroups } =
    useGetGroupsSelectOptions();

  const { getCompaniesSelectOptions, isLoadingAllCompanies } =
    useGetCompaniesSelectOptions({
      groupId: groupIdValue,
    });

  const { getBranchesSelectOptions, isLoadingAllBranches } =
    useGetBranchesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const { getAllVehiculesSelectOptions, isLoadingAllVehicules } =
    useGetVehiculesSelectOptions({
      groupId: groupIdValue,
      companyId: companyIdValue,
    });

  const numberOfCompanies = getCompaniesSelectOptions()?.length;
  const numberOfBranches = getBranchesSelectOptions()?.length;
  const numberOfVehicules = getAllVehiculesSelectOptions()?.length;

  const { mutate: mutateCreateVehicule, isPending: isPendingCreateVehicule } =
    useCreateVehicule({
      t,
      showDefaultSuccessMessage: false,
      handleSuccess: (data) => {
        setValue('vehiculeId' as Path<TSchema>, data?.['data']?.id);

        setLicensePlateNumberValue('');
        setVinValue('');
        setBrandValue('');
        setModelValue('');
      },
    });
  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('actionUserType' as Path<TSchema>, data.actionUserType ?? '');
      setValue('referenceNumber' as Path<TSchema>, data.referenceNumber ?? '');
      setValue('groupId' as Path<TSchema>, data.groupId ?? '');
      setValue('companyId' as Path<TSchema>, data.companyId ?? '');
      setValue('branchId' as Path<TSchema>, data.branchId ?? '');
      setValue('vehiculeId' as Path<TSchema>, data.vehiculeId ?? '');
      setValue('endDate' as Path<TSchema>, data.endDate ?? '');
      setValue(
        'humanModerationStatus' as Path<TSchema>,
        data.humanModerationStatus ?? HumanModerationStatus.A_MODERER,
      );
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Generate UUID for Reference Number ===
   */
  useEffect(() => {
    if (actionUserTypeValue === ActionUserType.INDIVIDUAL) {
      const referenceNumber = data?.referenceNumber || generateRandomUUID();

      setValue(
        'referenceNumber' as Path<TSchema>,
        referenceNumber as PathValue<TSchema, Path<TSchema>>,
      );
    } else {
      setValue(
        'referenceNumber' as Path<TSchema>,
        '' as PathValue<TSchema, Path<TSchema>>,
      );
    }
  }, [actionUserTypeValue, data?.referenceNumber, setValue]);
  /**
   * === END OF Generate UUID for Reference Number ===
   */

  /**
   * === Vehicule Select Functionality section ===
   */
  useEffect(() => {
    if (!assignedToVehicule) {
      setValue(
        'vehiculeId' as Path<TSchema>,
        '' as PathValue<TSchema, Path<TSchema>>,
      );
    } else {
      if (data?.vehiculeId) {
        setValue('vehiculeId' as Path<TSchema>, data?.vehiculeId);
      }
      setLicensePlateNumberValue('');
      setVinValue('');
      setBrandValue('');
      setModelValue('');
    }
  }, [assignedToVehicule, setValue, data?.vehiculeId]);
  /**
   * === END OF Vehicule Select Functionality section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (groupIdValue) {
      resetFieldError<TSchema>({ fieldName: 'groupId', setError });
    }

    if (companyIdValue) {
      resetFieldError<TSchema>({ fieldName: 'companyId', setError });
    }

    if (branchIdValue) {
      resetFieldError<TSchema>({ fieldName: 'branchId', setError });
    }

    if (actionUserTypeValue) {
      resetFieldError<TSchema>({ fieldName: 'actionUserType', setError });
    }

    if (referenceNumberValue) {
      resetFieldError<TSchema>({ fieldName: 'referenceNumber', setError });
    }

    if (vehiculeIdValue) {
      resetFieldError<TSchema>({ fieldName: 'vehiculeId', setError });
    }

    if (licensePlateNumberValue) {
      setLicensePlateNumberError('');
    }

    if (vinValue) {
      setVinError('');
    }

    if (brandValue) {
      setBrandError('');
    }

    if (modelValue) {
      setModelError('');
    }
  }, [
    groupIdValue,
    companyIdValue,
    branchIdValue,
    actionUserTypeValue,
    referenceNumberValue,
    licensePlateNumberValue,
    vinValue,
    brandValue,
    modelValue,
    setError,
    vehiculeIdValue,
  ]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!actionUserTypeValue) {
      setError('actionUserType' as Path<TSchema>, {
        message: t('accomplishmentTypeRequired'),
      });
    } else if (
      actionUserTypeValue === ActionUserType.INDIVIDUAL &&
      !referenceNumberValue
    ) {
      setError('referenceNumber' as Path<TSchema>, {
        message: t('referenceNumberRequired'),
      });
    }

    if (groupIsRequired && !groupIdValue) {
      setError('groupId' as Path<TSchema>, {
        message: t('common:groupRequired'),
      });
    }

    if (companyIsRequired && !companyIdValue) {
      setError('companyId' as Path<TSchema>, {
        message: t('common:companyRequired'),
      });
    }

    if (branchIsRequired && !branchIdValue) {
      setError('branchId' as Path<TSchema>, {
        message: t('common:branchRequired'),
      });
    }

    if (assignedToVehicule) {
      if (!vehiculeIdValue) {
        setError('vehiculeId' as Path<TSchema>, {
          message: t('common:vehiculeRequired'),
        });
      }
    } else {
      const vehiculeErrorMessages = existingVehicule?.data?.errorMessages;
      let hasLicensePlateError = false;
      let hasVinError = false;
      let hasBrandError = false;
      let hasModelError = false;

      if (licensePlateNumberValue) {
        if (!FRENCH_LICENSE_PLATE_NUMBER_REGEX.test(licensePlateNumberValue)) {
          setLicensePlateNumberError(
            t('common:licensePlateNumberFrenchIncorrect'),
          );
          hasLicensePlateError = true;
        } else if (
          vehiculeErrorMessages?.includes(
            FieldError.LICENSE_PLATE_NUMBER_WITH_GROUP_ID_EXISTS,
          )
        ) {
          setLicensePlateNumberError(
            t('licensePlateUsedBySameGroupChooseInsideFleet'),
          );
          hasLicensePlateError = true;
        } else if (
          vehiculeErrorMessages?.includes(
            FieldError.LICENSE_PLATE_NUMBER_WITH_COMPANY_ID_EXISTS,
          )
        ) {
          setLicensePlateNumberError(
            t('licensePlateUsedBySameCompanyChooseInsideFleet'),
          );
          hasLicensePlateError = true;
        }
      }

      if (vinValue) {
        if (!VIN_REGEX.test(vinValue)) {
          setVinError(t('common:vinIncorrect'));
          hasVinError = true;
        } else if (
          vehiculeErrorMessages?.includes(FieldError.VIN_WITH_GROUP_ID_EXISTS)
        ) {
          setVinError(t('vinUsedBySameGroupChooseInsideFleet'));
          hasVinError = true;
        } else if (
          vehiculeErrorMessages?.includes(FieldError.VIN_WITH_COMPANY_ID_EXISTS)
        ) {
          setVinError(t('vinUsedBySameCompanyChooseInsideFleet'));
          hasVinError = true;
        }
      }

      if (!brandValue) {
        setBrandError(t('common:brandRequired'));
        hasBrandError = true;
      }

      if (!modelValue) {
        setModelError(t('common:modelRequired'));
        hasModelError = true;
      }

      if (
        !hasLicensePlateError &&
        !hasVinError &&
        !hasBrandError &&
        !hasModelError
      ) {
        mutateCreateVehicule({
          licensePlateNumber: licensePlateNumberValue,
          vin: vinValue,
          brand: brandValue,
          model: modelValue,
          ownerType: groupIdValue
            ? VehiculeOwnerType.GROUP
            : companyIdValue
              ? VehiculeOwnerType.COMPANY
              : undefined,
          ownerGroupId: groupIdValue,
          ownerCompanyId: companyIdValue,
        });
      }
    }

    if (assignedToVehicule && allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('theInspection')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                isPendingCreateVehicule
                  ? `${t('common:loading')}...`
                  : t('common:save')
              }
              disabled={submitDisabled || isPendingCreateVehicule}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <>
            <FormField
              text={t('common:performedBy')}
              required
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <Controller
                  name="actionUserType"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      errorMessage={
                        errors && (errors.actionUserType?.message as string)
                      }
                      placeholder={t('common:chooseThePerformerType')}
                      options={[
                        {
                          value: ActionUserType.INDIVIDUAL,
                          label: t('common:individual'),
                        },
                        {
                          value: ActionUserType.PROFESSIONAL,
                          label: t('common:professional'),
                        },
                      ]}
                    />
                  )}
                />
              </div>
            </FormField>
            {actionUserTypeValue === ActionUserType.INDIVIDUAL && (
              <FormField
                text={t('common:referenceNumber_short_2')}
                required={actionUserTypeValue === ActionUserType.INDIVIDUAL}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  <Input
                    {...register('referenceNumber' as Path<TSchema>, {
                      setValueAs: (value: string) =>
                        value.replace(SPACE_REGEX, ''),
                    })}
                    errorMessage={
                      errors && (errors.referenceNumber?.message as string)
                    }
                    // Enforce to input only value to be without space
                    onInput={(e) =>
                      (e.currentTarget.value = e.currentTarget.value.replace(
                        SPACE_REGEX,
                        '',
                      ))
                    }
                    placeholder={t('common:referenceNumber_short_2')}
                    type="text"
                  />
                </div>
              </FormField>
            )}
            {canAccessGroups && (
              <FormField
                text={t('common:group')}
                required={groupIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllGroups ? (
                    <Select
                      placeholder={t('common:chooseTheGroup')}
                      options={[]}
                    />
                  ) : (
                    <Controller
                      name="groupId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.groupId?.message as string)
                          }
                          placeholder={t('common:chooseTheGroup')}
                          options={getGroupsSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessCompanies && (
              <FormField
                text={t('common:company')}
                required={companyIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllCompanies ? (
                    <Select
                      placeholder={t('common:chooseTheCompany')}
                      options={[]}
                    />
                  ) : !numberOfCompanies ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={t('common:noCompanyLinkedToThisGroup')}
                      />
                    </div>
                  ) : (
                    <Controller
                      name="companyId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.companyId?.message as string)
                          }
                          placeholder={t('common:chooseTheCompany')}
                          options={getCompaniesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            {canAccessBranches && (
              <FormField
                text={t('common:branch')}
                required={branchIsRequired}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  {isLoadingAllBranches ? (
                    <Select
                      placeholder={t('common:chooseTheBranch')}
                      options={[]}
                    />
                  ) : !numberOfBranches ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          groupIdValue
                            ? t('common:noBranchLinkedToThisGroup')
                            : t('common:noBranchLinkedToThisCompany')
                        }
                      />
                    </div>
                  ) : (
                    <Controller
                      name="branchId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.branchId?.message as string)
                          }
                          placeholder={t('common:chooseTheBranch')}
                          options={getBranchesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            )}
            <FormField
              text={t('linkedToAVehiculeOfTheFleet')}
              textWrapperClassName="mt-2"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                <Switch
                  onChange={() =>
                    setAssignedToVehicule((prevState) => !prevState)
                  }
                  checked={assignedToVehicule}
                />
              </div>
            </FormField>
            {assignedToVehicule ? (
              <FormField
                text={t('common:vehicule')}
                required={assignedToVehicule && numberOfVehicules > 0}
                textWrapperClassName="mt-5"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5 mt-2">
                  {isLoadingAllVehicules ? (
                    <Select
                      placeholder={t('common:chooseTheVehicule')}
                      options={[]}
                    />
                  ) : !numberOfVehicules ? (
                    <div className="mt-3">
                      <NotFound
                        containerClassName="gap-1.5"
                        iconClassName="-mt-1.5 text-lg"
                        textClassName="text-sm"
                        text={
                          branchIdValue
                            ? t('noVehiculeBelongsToThisBranch')
                            : companyIdValue
                              ? t('noVehiculeBelongsToThisCompany')
                              : t('noVehiculeBelongsToThisGroup')
                        }
                      />
                    </div>
                  ) : (
                    <Controller
                      name="vehiculeId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          errorMessage={
                            errors && (errors.vehiculeId?.message as string)
                          }
                          placeholder={t('common:chooseTheVehicule')}
                          options={getAllVehiculesSelectOptions()}
                        />
                      )}
                    />
                  )}
                </div>
              </FormField>
            ) : (
              <>
                <FormField
                  text={t('common:licensePlateNumber')}
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={licensePlateNumberValue}
                      onChange={(e) =>
                        setLicensePlateNumberValue(e.target.value)
                      }
                      errorMessage={licensePlateNumberError}
                      placeholder={t('common:licensePlateNumber')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField text={t('common:vin')} textWrapperClassName="mt-3">
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={vinValue}
                      onChange={(e) => setVinValue(e.target.value)}
                      errorMessage={vinError}
                      placeholder={t('common:vin')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:brand')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={brandValue}
                      onChange={(e) => setBrandValue(e.target.value)}
                      errorMessage={brandError}
                      placeholder={t('common:brand')}
                      type="text"
                    />
                  </div>
                </FormField>
                <FormField
                  text={t('common:model')}
                  required
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <Input
                      value={modelValue}
                      onChange={(e) => setModelValue(e.target.value)}
                      errorMessage={modelError}
                      placeholder={t('common:model')}
                      type="text"
                    />
                  </div>
                </FormField>
              </>
            )}
            <FormField
              text={t('endOfInspectionDate')}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5">
                <div className="flex flex-1 flex-col gap-0.75">
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker<'endDate'>
                        field={field}
                        variant="mobile"
                        useTime={false}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          field.onChange(date?.toDate() || null);
                        }}
                      />
                    )}
                  />
                  <ErrorMessage
                    message={errors && (errors.endDate?.message as string)}
                  />
                </div>
              </div>
            </FormField>
            <FormField
              text={`${t('common:moderated')} ?`}
              textWrapperClassName="mt-3"
            >
              <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
                <Switch
                  errorMessage={
                    errors && (errors.humanModerationStatus?.message as string)
                  }
                  checked={
                    humanModerationStatusValue === HumanModerationStatus.MODERE
                  }
                  onChange={(e) => {
                    setValue?.(
                      'humanModerationStatus' as Path<TSchema>,
                      (e.target.checked
                        ? HumanModerationStatus.MODERE
                        : HumanModerationStatus.A_MODERER) as PathValue<
                        TSchema,
                        Path<TSchema>
                      >,
                    );
                  }}
                />
              </div>
            </FormField>
          </>
        </Card>
      </form>
      {isPendingCreateVehicule && <Loader />}
    </>
  );
};
